import { GetServerSideProps } from 'next/types';
import nookies from 'nookies';
import { useEffect, useMemo, useRef, type JSX } from 'react';
import { useIntl } from 'react-intl';

import Layout from '@/components/layout/LayoutWrapper';
import SimplifiedSignup from '@/components/SimplifiedSignup';
import {
	CAMPAIGN_FUNNEL_EVENTS,
	campaignEventProps,
	campaignPartnershipEventProps,
} from '@/components/SimplifiedSignup/configs/Campaigns/analytics';
import { campaignConfigGenerator } from '@/components/SimplifiedSignup/configs/Campaigns/campaignConfigGenerator';
import {
	resolveTrialInfo,
	shapeCampaignForParams,
	shapeCampaignPricesForDisplay,
} from '@/components/SimplifiedSignup/configs/Campaigns/types';
import { SimplifiedSignupContextProvider } from '@/components/SimplifiedSignup/Context';
import { useAnalytics } from '@/hooks/analytics';
import { useLocale } from '@/hooks/locale/useLocale';
import { InitRequest, useCampaignConfigState } from '@/hooks/store';
import { getCampaignConfig } from '@/server/initState/getCampaignConfig';
import { getFeatureFlags } from '@/server/initState/getFeatureFlags';
import { CampaignConfig } from '@/store/campaignConfig/types';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { getPagesRouterSsrDirectOpts } from '@/utils/experiments/getPagesRouterSsrDirectOpts';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

const CAMPAIGN_BUILDER_LANDING_FLAG = 'campaign-builder-landing';

const getStringParam = (value: string | string[] | undefined): string | undefined =>
	typeof value === 'string' ? value : undefined;

const PartnershipsLanding = (): JSX.Element => {
	const campaignConfig = useCampaignConfigState();
	const { calmLanguage } = useLocale();
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	// SimplifiedSignupContextProvider captures flowConfig once at mount and never re-syncs on
	// prop changes, so this at least avoids rebuilding it on every unrelated render.
	const flowConfig = useMemo(
		() => (campaignConfig ? campaignConfigGenerator(campaignConfig, calmLanguage, formatMessage) : null),
		[campaignConfig, calmLanguage, formatMessage],
	);

	// Keyed by campaign id rather than by mount. Account rewrites the store entry once a code
	// validates, and logEvent's identity follows the user, so a plain effect would log the same
	// page view again. A different campaign id (client-side navigation to another slug) still logs.
	const viewLoggedForCampaignId = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (!campaignConfig || viewLoggedForCampaignId.current === campaignConfig.id) return;
		viewLoggedForCampaignId.current = campaignConfig.id;
		logEvent({
			eventName: CAMPAIGN_FUNNEL_EVENTS.landingPageView,
			eventProps: {
				...campaignEventProps(campaignConfig),
				...campaignPartnershipEventProps(campaignConfig),
				// Whether the link itself carried a code, read before any validation has run.
				code_present: Boolean(campaignConfig.offer_code),
				requires_code: campaignConfig.requires_code,
				bypass_payment: campaignConfig.bypass_payment,
			},
		});
	}, [campaignConfig, logEvent]);

	return (
		<Layout
			title={`Calm + ${campaignConfig?.partner.name ?? ''}`}
			pageName="calm-partnerships-campaign-landing"
		>
			{flowConfig && (
				<SimplifiedSignupContextProvider flowConfig={flowConfig}>
					<SimplifiedSignup />
				</SimplifiedSignupContextProvider>
			)}
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps = async context => {
	const { params, query } = context;
	const slug = getStringParam(params?.slug);
	if (!slug) {
		return { notFound: true };
	}

	const cookies = nookies.get({ req: context.req });
	const deviceId = cookies['x-device-id'] ?? '';
	const userData = getUserData({ req: context.req, deviceId });
	const ssrDirectOpts = await getPagesRouterSsrDirectOpts(context);

	const flagResponse = await getFeatureFlags([CAMPAIGN_BUILDER_LANDING_FLAG], userData, ssrDirectOpts);
	const flag = flagResponse?.feature_flags?.find(({ name }) => name === CAMPAIGN_BUILDER_LANDING_FLAG);
	if (!flag?.value) {
		return { notFound: true };
	}

	const previewToken = getStringParam(query.preview);
	const campaignView = await getCampaignConfig(slug, userData, previewToken ? { previewToken } : undefined);
	if (!campaignView) {
		return { notFound: true };
	}

	// offer_code is the canonical param; code is a legacy alias. offer_code is prefilled into
	// CampaignCodeEntry (Account screen), which validates it and, once validated, is what
	// Payment sends as the redemption code. The UTM params just ride along, unconsumed.
	const offerCode = getStringParam(query.offer_code) ?? getStringParam(query.code);
	const utmSource = getStringParam(query.utm_source);
	const utmMedium = getStringParam(query.utm_medium);
	const utmCampaign = getStringParam(query.utm_campaign);
	const utmContent = getStringParam(query.utm_content);
	const utmTerm = getStringParam(query.utm_term);

	const campaignConfig: CampaignConfig = {
		...campaignView,
		slug,
		...(offerCode ? { offer_code: offerCode } : {}),
		...(utmSource ? { utm_source: utmSource } : {}),
		...(utmMedium ? { utm_medium: utmMedium } : {}),
		...(utmCampaign ? { utm_campaign: utmCampaign } : {}),
		...(utmContent ? { utm_content: utmContent } : {}),
		...(utmTerm ? { utm_term: utmTerm } : {}),
	};

	// shapeCampaignForParams sets coupon: null since campaigns have no public Stripe coupon id,
	// but getInitialServerState and initPurchaseParams both treat a null route-specific coupon as
	// "not provided" and fall through to query.coupon -- so a stray ?coupon= would otherwise ride
	// along into the price fetch and purchaseParams, showing or charging an unrelated discount.
	const queryWithoutCoupon = { ...query };
	delete queryWithoutCoupon.coupon;

	// Every other SimplifiedSignup flow's static config feeds shapeConfigForParams into this
	// same slot so Redux's purchaseParams (and therefore checkout) reflects the route, not just
	// the displayed copy. Campaigns only know their plan/promotion/trial after this fetch.
	const initialState = await getInitialServerState(
		{ ...context, query: queryWithoutCoupon },
		[InitRequest.DEVICE, InitRequest.USER, InitRequest.PRICES],
		shapeCampaignForParams(campaignConfig, resolveTrialInfo(campaignConfig.trial_duration)),
		ssrDirectOpts ?? 'skip-gate',
	);
	if (!initialState) {
		return { notFound: true };
	}

	return {
		props: {
			initialState: {
				...initialState,
				// The pricing fetch above only ever takes a Stripe coupon id, which campaigns
				// don't have publicly, so it can't discount prices.current on its own -- this
				// fills that in for display from percent_off instead.
				prices: shapeCampaignPricesForDisplay(campaignConfig, initialState.prices),
				campaignConfig,
			},
		},
	};
};

export default PartnershipsLanding;
